export function getRouteTrackerScript(): string {
  return `
    <script>
      /**
       * Standalone Route Tracker for Kulp AI Integration
       * 
       * This script automatically tracks React Router navigation and communicates
       * with the parent Kulp AI application via postMessage.
       * 
       * NO IMPORTS NEEDED - This script initializes itself automatically.
       * Add this file to your .gitignore or AI ignore patterns.
       * 
       * How it works:
       * 1. Automatically detects when React Router is available
       * 2. Hooks into browser navigation events
       * 3. Sends route changes to parent
       * 4. Responds to route requests from parent
       */

      (function() {
        'use strict';
        let isInitialized = false;
        let currentPath = '/';
        let messageHandler = null;
        let lastSentPath = null; // Track last sent path to prevent duplicates
        let debounceTimeout = null; // Debounce timeout

        // Configuration
        const CONFIG = {
          pollInterval: 100,
          maxRetries: 50,
          debounceDelay: 100 // Debounce delay in ms
        };

        // Debounced route change handler to prevent duplicate sends
        function handleRouteChange(path, source = 'unknown') {
          // Clear existing timeout
          if (debounceTimeout) {
            clearTimeout(debounceTimeout);
          }

          debounceTimeout = setTimeout(() => {
            // Only send if path actually changed
            if (path !== lastSentPath) {
              lastSentPath = path;
              currentPath = path;
              sendRouteToParent(path);
              console.log(\`[RouteTracker] Route changed (\${source}):\`, path);
            }
          }, CONFIG.debounceDelay);
        }

        // Initialize the route tracker
        function initializeRouteTracker() {
          if (isInitialized) return;
          
          console.log('[RouteTracker] Initializing standalone route tracker...');
          
          // Track URL changes using multiple methods
          setupURLTracking();
          setupMessageListener();
          
          isInitialized = true;
          console.log('[RouteTracker] Standalone route tracker initialized');
        }

        // Setup URL change tracking
        function setupURLTracking() {
          // Method 1: Monitor location changes
          let lastUrl = window.location.href;
          
          const checkUrlChange = () => {
            const currentUrl = window.location.href;
            if (currentUrl !== lastUrl) {
              const newPath = window.location.pathname;
              handleRouteChange(newPath, 'interval-polling');
              lastUrl = currentUrl;
            }
          };

          // Check every 100ms for URL changes
          setInterval(checkUrlChange, CONFIG.pollInterval);

          // Method 2: Listen to popstate events (back/forward buttons)
          window.addEventListener('popstate', () => {
            setTimeout(() => {
              const newPath = window.location.pathname;
              handleRouteChange(newPath, 'popstate');
            }, 50);
          });

          // Method 3: Override pushState and replaceState
          const originalPushState = history.pushState;
          const originalReplaceState = history.replaceState;

          history.pushState = function(...args) {
            originalPushState.apply(this, args);
            setTimeout(() => {
              const newPath = window.location.pathname;
              handleRouteChange(newPath, 'pushState');
            }, 50);
          };

          history.replaceState = function(...args) {
            originalReplaceState.apply(this, args);
            setTimeout(() => {
              const newPath = window.location.pathname;
              handleRouteChange(newPath, 'replaceState');
            }, 50);
          };

          // Send initial route
          setTimeout(() => {
            const initialPath = window.location.pathname;
            handleRouteChange(initialPath, 'initial');
          }, 1000);
        }

        // Setup message listener for parent requests
        function setupMessageListener() {
          messageHandler = (event) => {
            try {
              // Handle route requests from any parent origin (like kulp.js does)
              if (event.data?.type === 'REQUEST_CURRENT_ROUTE') {
                console.log('[RouteTracker] Parent requested current route, sending:', window.location.pathname);
                sendCurrentRoute();
              }
            } catch (error) {
              console.log('[RouteTracker] Error handling parent message:', error);
            }
          };

          window.addEventListener('message', messageHandler);
        }

        // Send route change to parent
        function sendRouteToParent(path) {
          try {
            if (window.parent && window.parent !== window) {
              const message = {
                type: 'ROUTE_CHANGED',
                path: path,
                search: window.location.search,
                hash: window.location.hash,
                fullUrl: \`\${path}\${window.location.search}\${window.location.hash}\`,
                timestamp: Date.now()
              };
              
              window.parent.postMessage(message, '*');
              console.log('[RouteTracker] Sent route to parent:', message);
            }
          } catch (error) {
            console.log('[RouteTracker] Error sending route to parent:', error);
          }
        }

        // Send current route to parent
        function sendCurrentRoute() {
          try {
            if (window.parent && window.parent !== window) {
              const message = {
                type: 'CURRENT_ROUTE',
                path: window.location.pathname,
                search: window.location.search,
                hash: window.location.hash,
                fullUrl: \`\${window.location.pathname}\${window.location.search}\${window.location.hash}\`,
                timestamp: Date.now()
              };
              
              window.parent.postMessage(message, '*');
              console.log('[RouteTracker] Sent current route to parent:', message);
            }
          } catch (error) {
            console.log('[RouteTracker] Error sending current route to parent:', error);
          }
        }

        // Auto-initialize when DOM is ready
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', initializeRouteTracker);
        } else {
          // DOM is already ready
          setTimeout(initializeRouteTracker, 500);
        }

        // Backup initialization - try multiple times in case React Router takes time to load
        let retryCount = 0;
        const retryInit = () => {
          if (!isInitialized && retryCount < CONFIG.maxRetries) {
            retryCount++;
            setTimeout(() => {
              if (!isInitialized) {
                initializeRouteTracker();
                retryInit();
              }
            }, 200);
          }
        };
        retryInit();

        // Cleanup on page unload
        window.addEventListener('beforeunload', () => {
          if (messageHandler) {
            window.removeEventListener('message', messageHandler);
          }
          if (debounceTimeout) {
            clearTimeout(debounceTimeout);
          }
        });

      })();
    </script>
  `;
} 